import React from 'react';
import {
  EnvelopeIcon,
  DevicePhoneMobileIcon,
  ArrowPathIcon,
  ChatBubbleLeftIcon,
  DocumentTextIcon,
  CalendarIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import { FilterChip } from './FilterChip';
import type { CommunicationType } from '../../types/tracker.types';
import './ActivityFilters.styles.css';

/**
 * Filter configuration
 */
interface FilterConfig {
  type: CommunicationType | 'all' | 'sms';
  label: string;
  icon?: React.ReactNode;
}

/**
 * Filter configurations
 */
const FILTER_OPTIONS: FilterConfig[] = [
  { type: 'all', label: 'Tous' },
  {
    type: 'email',
    label: 'Emails',
    icon: <EnvelopeIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'sms',
    label: 'SMS',
    icon: <DevicePhoneMobileIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'status_update',
    label: 'Statuts',
    icon: <ArrowPathIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'com_post_it',
    label: 'Post-it',
    icon: <DocumentTextIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'com_internes',
    label: 'Internes',
    icon: <ChatBubbleLeftIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'tracker_com',
    label: 'Messages',
    icon: <ChatBubbleLeftIcon className="w-3.5 h-3.5" />,
  },
  {
    type: 'rdv',
    label: 'RDV',
    icon: <CalendarIcon className="w-3.5 h-3.5" />,
  },
];

/**
 * Props for ActivityFilters component
 */
interface ActivityFiltersProps {
  /** Currently enabled types */
  enabledTypes: Set<CommunicationType>;

  /** Toggle a type on/off */
  onToggleType: (type: CommunicationType) => void;

  /** Show all types */
  onShowAll: () => void;

  /** Select only a specific type (exclusive mode) */
  onSelectOnly?: (type: CommunicationType) => void;

  /** Counts per type (optional) */
  typeCounts?: Record<CommunicationType | 'sms', number>;

  /** Total count (optional) */
  totalCount?: number;

  /** Whether filters are active (for reset button) */
  hasActiveFilters?: boolean;

  /** Reset all filters */
  onReset?: () => void;

  /** Search query */
  searchQuery?: string;

  /** Search query change handler */
  onSearchChange?: (query: string) => void;

  /** Export the client timeline to PDF */
  onExportPdf?: () => void;

  /** Whether the PDF is currently being generated (AI enrichment) */
  exportingPdf?: boolean;
}

/**
 * ActivityFilters Component
 *
 * Filter chips bar for activity types
 */
export const ActivityFilters: React.FC<ActivityFiltersProps> = ({
  enabledTypes,
  onToggleType,
  onShowAll,
  onSelectOnly,
  typeCounts,
  totalCount,
  hasActiveFilters,
  onReset,
  searchQuery = '',
  onSearchChange,
  onExportPdf,
  exportingPdf = false,
}) => {
  // Check if "All" should be active (all types enabled)
  const allTypesEnabled =
    enabledTypes.has('email') &&
    (enabledTypes.has('sms_outbox') || enabledTypes.has('sms_inbox')) &&
    enabledTypes.has('status_update') &&
    enabledTypes.has('com_post_it') &&
    enabledTypes.has('com_internes') &&
    enabledTypes.has('tracker_com') &&
    enabledTypes.has('rdv');

  // Handle "All" click - always show all (clear filters)
  const handleAllClick = () => {
    onShowAll();
  };

  // Handle type toggle
  // When all types are enabled and user clicks a specific type, switch to exclusive mode
  const handleTypeToggle = (filterType: CommunicationType | 'all' | 'sms') => {
    if (filterType === 'all') {
      handleAllClick();
    } else if (filterType === 'sms') {
      // When "Tous" is active, switch to SMS only
      if (allTypesEnabled && onSelectOnly) {
        onSelectOnly('sms_outbox');
        onSelectOnly('sms_inbox');
        return;
      }
      // Toggle both SMS types together
      const smsEnabled = enabledTypes.has('sms_outbox') || enabledTypes.has('sms_inbox');
      if (smsEnabled) {
        if (enabledTypes.has('sms_outbox')) onToggleType('sms_outbox');
        if (enabledTypes.has('sms_inbox')) onToggleType('sms_inbox');
      } else {
        if (!enabledTypes.has('sms_outbox')) onToggleType('sms_outbox');
        if (!enabledTypes.has('sms_inbox')) onToggleType('sms_inbox');
      }
    } else {
      // When "Tous" is active, switch to this type only
      if (allTypesEnabled && onSelectOnly) {
        onSelectOnly(filterType);
      } else {
        onToggleType(filterType);
      }
    }
  };

  // Check if a filter type is active (for visual styling)
  // When all types are enabled ("Tous" mode), only "Tous" should be highlighted
  // When filtering, only the selected individual chips should be highlighted
  const isTypeActive = (filterType: CommunicationType | 'all' | 'sms'): boolean => {
    if (filterType === 'all') {
      return allTypesEnabled;
    }
    // Individual chips only show as active when we're NOT in "Tous" mode
    if (allTypesEnabled) {
      return false;
    }
    if (filterType === 'sms') {
      return enabledTypes.has('sms_outbox') || enabledTypes.has('sms_inbox');
    }
    return enabledTypes.has(filterType);
  };

  // Get count for a filter type
  const getTypeCount = (filterType: CommunicationType | 'all' | 'sms'): number | undefined => {
    if (!typeCounts) return undefined;
    if (filterType === 'all') return totalCount;
    return typeCounts[filterType as keyof typeof typeCounts];
  };

  return (
    <div className="activity-filters">
      {/* Left section: Segmented control */}
      <div className="activity-filters-left">
        {/* Segmented control container */}
        <div className="activity-filters-segmented" role="tablist" aria-label="Filtrer les activités">
          {FILTER_OPTIONS.map((filter) => (
            <FilterChip
              key={filter.type}
              label={filter.label}
              icon={filter.icon}
              isActive={isTypeActive(filter.type)}
              count={getTypeCount(filter.type)}
              onClick={() => handleTypeToggle(filter.type)}
            />
          ))}
        </div>

        {/* Reset button */}
        {hasActiveFilters && onReset && (
          <button className="activity-filters-reset" onClick={onReset}>
            Réinitialiser
          </button>
        )}
      </div>

      {/* Right section */}
      <div className="activity-filters-right">
        {onExportPdf && (
          <button
            className="activity-filters-more-btn"
            aria-label="Exporter la frise du dossier en PDF"
            title="Exporter la frise de suivi (PDF)"
            onClick={onExportPdf}
            disabled={exportingPdf}
          >
            <ArrowDownTrayIcon />
            <span>{exportingPdf ? 'Génération…' : 'Exporter PDF'}</span>
          </button>
        )}
        {/* Filtres button (placeholder for popover) */}
        <button className="activity-filters-more-btn" aria-label="Plus de filtres">
          <FunnelIcon />
          <span>Filtres</span>
        </button>
      </div>
    </div>
  );
};

export default ActivityFilters;
